import React, { useState } from 'react';
import { 
  Steps, 
  Card, 
  Form, 
  Input, 
  Select, 
  DatePicker, 
  Upload, 
  Button, 
  Row, 
  Col, 
  message, 
  Typography,
  Space,
  Result,
  QRCode,
  Alert,
  Descriptions
} from 'antd';
import { 
  UserOutlined, 
  BankOutlined, 
  SafetyOutlined,
  UploadOutlined,
  QrcodeOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined
} from '@ant-design/icons';
import { useAccount } from '../../context/AccountContext';
import LedgerProgress from './LedgerProgress';

const { Title, Text } = Typography;
const { Option } = Select;

interface StepContentProps {
  current: number;
  onNext: () => void;
  onPrev: () => void;
}

// 步骤1：会员开立
const MemberRegistrationStep: React.FC<StepContentProps> = ({ onNext }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (values: any) => {
    setLoading(true);
    try {
      // 模拟提交会员开立申请
      await new Promise(resolve => setTimeout(resolve, 2000));
      message.success('会员开立申请提交成功！');
      onNext();
    } catch (error) {
      message.error('提交失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card title="第一步：会员开立" className="step-card">
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        requiredMark={false}
      >
        <Title level={4}>营业执照信息</Title>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item 
              name="org_inst_cd" 
              label="组织机构代码"
            >
              <Input placeholder="请输入组织机构代码" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item 
              name="cmpy_nme" 
              label="单位名称"
            >
              <Input placeholder="请输入单位名称" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item 
              name="cmpy_id_tp" 
              label="单位证件种类"
            >
              <Select placeholder="请选择证件种类">
                <Option value="2011">营业执照（统一社会信用代码）</Option>
                <Option value="2040">事业单位法人证书</Option>
                <Option value="2020">组织机构代码证</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item 
              name="cmpy_id_no" 
              label="单位证件号码"
            >
              <Input placeholder="请输入证件号码" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item 
              name="cmpy_id_due_dt" 
              label="单位证件过期时间"
            >
              <DatePicker style={{ width: '100%' }} placeholder="请选择过期时间" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item 
              name="bsn_lic_no" 
              label="营业执照号码"
            >
              <Input placeholder="请输入营业执照号码" />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item 
          name="rgs_addr" 
          label="注册地址"
        >
          <Input.TextArea rows={2} placeholder="请输入注册地址" />
        </Form.Item>

        <Form.Item 
          name="opr_addr" 
          label="经营地址"
        >
          <Input.TextArea rows={2} placeholder="请输入经营地址" />
        </Form.Item>

        <Form.Item 
          name="opr_scope" 
          label="经营范围"
        >
          <Input.TextArea rows={3} placeholder="请输入经营范围" />
        </Form.Item>

        <Title level={4} style={{ marginTop: 24 }}>法定代表人信息</Title>
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item 
              name="lgl_rprs_nme" 
              label="法定代表人姓名"
            >
              <Input placeholder="请输入法人姓名" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item 
              name="lgl_rprs_id_tp" 
              label="法定代表人证件种类"
            >
              <Select placeholder="请选择证件种类">
                <Option value="1101">中华人民共和国居民身份证</Option>
                <Option value="1102">中华人民共和国临时居民身份证</Option>
                <Option value="1111">普通护照</Option>
                <Option value="1112">外交护照</Option>
                <Option value="1121">港澳居民来往内地通行证</Option>
                <Option value="1131">台湾居民来往大陆通行证</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item 
              name="lgl_rprs_id_no" 
              label="法定代表人证件号"
            >
              <Input placeholder="请输入法人证件号" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={8}>
            <Form.Item 
              name="lgl_rprs_id_vld_dt" 
              label="法定代表人证件有效期"
            >
              <DatePicker style={{ width: '100%' }} placeholder="请选择有效期" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item 
              name="lgl_rprs_ctzn" 
              label="法定代表人国籍"
            >
              <Select placeholder="请选择国籍">
                <Option value="CN">中国</Option>
                <Option value="US">美国</Option>
                <Option value="JP">日本</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item 
              name="lgl_rprs_phone_no" 
              label="法定代表人手机号"
            >
              <Input placeholder="请输入法人手机号" />
            </Form.Item>
          </Col>
        </Row>

        <Title level={4} style={{ marginTop: 24 }}>授权代理人信息</Title>
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item 
              name="ath_agnt_nme" 
              label="代理人姓名"
            >
              <Input placeholder="请输入代理人姓名" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item 
              name="ath_agnt_id_tp" 
              label="代理人证件种类"
            >
              <Select placeholder="请选择证件种类">
                <Option value="1101">中华人民共和国居民身份证</Option>
                <Option value="1102">中华人民共和国临时居民身份证</Option>
                <Option value="1111">普通护照</Option>
                <Option value="1112">外交护照</Option>
                <Option value="1121">港澳居民来往内地通行证</Option>
                <Option value="1131">台湾居民来往大陆通行证</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item 
              name="ath_agnt_id_no" 
              label="代理人证件号"
            >
              <Input placeholder="请输入代理人证件号" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={8}>
            <Form.Item 
              name="ath_agnt_id_vld_dt" 
              label="代理人证件有效期"
            >
              <DatePicker style={{ width: '100%' }} placeholder="请选择有效期" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item 
              name="ath_agnt_ctzn" 
              label="代理人国籍"
            >
              <Select placeholder="请选择国籍">
                <Option value="CN">中国</Option>
                <Option value="US">美国</Option>
                <Option value="JP">日本</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item 
              name="ath_agnt_mob_no" 
              label="代理人手机号"
            >
              <Input placeholder="请输入代理人手机号" />
            </Form.Item>
          </Col>
        </Row>

        <Title level={4} style={{ marginTop: 24 }}>影像资料</Title>
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item 
              name="businessLicenseImage" 
              label="营业执照照片"
            >
              <Upload>
                <Button icon={<UploadOutlined />}>点击上传</Button>
              </Upload>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item 
              name="legalPersonIdImage" 
              label="法定代表人证件照"
            >
              <Upload>
                <Button icon={<UploadOutlined />}>点击上传</Button>
              </Upload>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item 
              name="agentIdImage" 
              label="代理人证件照"
            >
              <Upload>
                <Button icon={<UploadOutlined />}>点击上传</Button>
              </Upload>
            </Form.Item>
          </Col>
        </Row>

        <Form.Item 
          name="authorizationLetter" 
          label="授权书"
        >
          <Upload>
            <Button icon={<UploadOutlined />}>点击上传授权书</Button>
          </Upload>
        </Form.Item>

        <Form.Item style={{ textAlign: 'center', marginTop: 32 }}>
          <Button 
            type="primary" 
            htmlType="submit" 
            size="large" 
            loading={loading}
            style={{ minWidth: 120 }}
          >
            提交申请
          </Button>
        </Form.Item>
      </Form>
    </Card>
  );
};

// 步骤2：登记簿开立
const LedgerRegistrationStep: React.FC<StepContentProps & { onReopen: () => void }> = ({ onNext, onReopen }) => {
  const [qrCodeVisible, setQrCodeVisible] = useState(false);
  const [showProgress, setShowProgress] = useState(false);
  const [showFailure, setShowFailure] = useState(false);
  const [demoStatus, setDemoStatus] = useState<'progress' | 'success' | 'failure'>('progress');

  const handleGenerateQR = () => {
    setQrCodeVisible(true);
  };

  const handleConfirmAuth = async () => {
    try {
      // 模拟扫码授权验证过程
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // 根据演示状态选择跳转
      if (demoStatus === 'success') {
        message.success('登记簿开立成功！');
        setTimeout(onNext, 1000); // 直接跳转到绑卡
      } else if (demoStatus === 'failure') {
        message.error('登记簿开立失败！');
        setShowFailure(true);
      } else {
        // 默认进入进度页面
        setShowProgress(true);
        message.success('授权验证完成！正在查询登记簿开立进度...');
      }
    } catch (error) {
      message.error('授权验证失败，请重试');
    }
  };

  const handleReauthorize = () => {
    setShowProgress(false);
    setShowFailure(false);
    setQrCodeVisible(false);
    message.info('请重新进行授权验证');
  };

  // 重新生成二维码功能
  const handleRegenerateQR = () => {
    // 模拟重新生成二维码
    message.success('二维码已重新生成！');
    // 这里可以添加实际的二维码重新生成逻辑
  };

  if (showProgress) {
    return (
      <LedgerProgress 
        onSuccess={onNext}
        onReauthorize={handleReauthorize}
      />
    );
  }

  if (showFailure) {
    return (
      <Card title="登记簿开立失败" className="step-card">
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <Result
            status="error"
            title="登记簿开立失败"
            subTitle="企业信息审核不通过，请检查企业信息后重新申请"
            extra={[
              <Button type="primary" key="reopen" onClick={onReopen}>
                重新开立账户
              </Button>,
              <Button key="contact" onClick={() => message.info('请联系客服：400-123-4567')}>
                联系客服
              </Button>
            ]}
          />
          <div style={{ marginTop: 24, textAlign: 'left', maxWidth: 600, margin: '24px auto 0' }}>
            <Alert
              message="常见失败原因"
              description={
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  <li>营业执照信息与银行系统记录不一致</li>
                  <li>法定代表人身份信息验证失败</li>
                  <li>企业经营状态异常或已注销</li>
                  <li>企业存在不良信用记录</li>
                  <li>提交的材料不清晰或信息不完整</li>
                </ul>
              }
              type="warning"
              showIcon
            />
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card title="第二步：登记簿开立" className="step-card">
      <div style={{ textAlign: 'center', padding: '40px 0' }}>
        {!qrCodeVisible ? (
          <Space direction="vertical" size="large">
            <QrcodeOutlined style={{ fontSize: 64, color: '#1890ff' }} />
            <Title level={3}>准备开立登记簿</Title>
            <Text type="secondary">
              会员信息已提交成功，现在需要通过交行小程序进行身份验证以开立登记簿
            </Text>
            <Alert
              message="重要提示"
              description="登记簿开立需要银行系统审核，授权完成后请耐心等待审核结果"
              type="info"
              showIcon
              style={{ textAlign: 'left' }}
            />
            <Button 
              type="primary" 
              size="large" 
              onClick={handleGenerateQR}
              icon={<QrcodeOutlined />}
            >
              生成授权二维码
            </Button>
          </Space>
        ) : (
          <Space direction="vertical" size="large">
            <Title level={3}>扫码授权</Title>
            <QRCode value="https://bank.comm.cn/auth/ledger/123456" size={200} />
            <Alert
              message="请使用交通银行APP扫描二维码"
              description="扫码后将跳转到交行小程序进行身份验证和授权确认"
              type="info"
              showIcon
            />
            
            {/* 演示模式状态选择器 - 悬浮半透明样式 */}
            <div style={{
              position: 'absolute',
              top: 20,
              right: 20,
              backgroundColor: 'rgba(240, 248, 255, 0.9)',
              border: '1px solid #d9d9d9',
              borderRadius: 8,
              padding: 12,
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              zIndex: 10,
              minWidth: 200
            }}>
              <Space direction="vertical" size={8} style={{ width: '100%' }}>
                <Text type="secondary" style={{ fontSize: '12px', fontWeight: 'bold' }}>
                  演示状态选择
                </Text>
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Button 
                    size="small" 
                    type={demoStatus === 'progress' ? 'primary' : 'default'}
                    onClick={() => setDemoStatus('progress')}
                    block
                    style={{ textAlign: 'left' }}
                  >
                    📊 进度查询页面
                  </Button>
                  <Button 
                    size="small" 
                    type={demoStatus === 'success' ? 'primary' : 'default'}
                    onClick={() => setDemoStatus('success')}
                    block
                    style={{ textAlign: 'left' }}
                  >
                    ✅ 直接成功（绑卡）
                  </Button>
                  <Button 
                    size="small" 
                    type={demoStatus === 'failure' ? 'primary' : 'default'}
                    onClick={() => setDemoStatus('failure')}
                    block
                    style={{ textAlign: 'left' }}
                  >
                    ❌ 开户失败页面
                  </Button>
                </Space>
                <Text type="secondary" style={{ fontSize: '11px' }}>
                  当前：{demoStatus === 'progress' ? '进度查询' : demoStatus === 'success' ? '直接成功' : '开户失败'}
                </Text>
              </Space>
            </div>

            <div style={{ marginTop: 16 }}>
              <Text type="secondary">完成扫码后，请点击下方按钮</Text>
            </div>
            <Button 
              type="primary" 
              size="large" 
              onClick={handleConfirmAuth}
              style={{ marginTop: 16 }}
            >
              我已完成授权
            </Button>
            
            {/* 重新生成二维码按钮 - 放在二维码下方 */}
            <div style={{ marginTop: 8, textAlign: 'center' }}>
              <Button 
                onClick={handleRegenerateQR}
                size="small"
                type="link"
                icon={<QrcodeOutlined />}
              >
                重新生成二维码
              </Button>
            </div>
          </Space>
        )}
      </div>
    </Card>
  );
};

// 步骤3：银行卡绑定
const CardBindingStep: React.FC<StepContentProps> = ({ onNext }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [bindingStatus, setBindingStatus] = useState<'form' | 'pending' | 'reminder'>('form');
  const [verificationAmount] = useState(() => {
    // 生成固定的验证金额（0.01-0.99元之间的随机金额）
    return (Math.random() * 0.98 + 0.01).toFixed(2);
  });

  const handleSubmit = async (values: any) => {
    setLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 2000));
      setBindingStatus('pending');
      message.success('银行卡信息提交成功！');
      
      // 模拟48小时倒计时提醒
      setTimeout(() => {
        setBindingStatus('reminder');
      }, 3000);
    } catch (error) {
      message.error('提交失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteBinding = () => {
    message.success('银行卡绑定验证完成！账户开立流程全部完成');
    onNext();
  };

  if (bindingStatus === 'pending') {
    return (
      <Card title="第三步：银行卡绑定" className="step-card">
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <Result
            icon={<ClockCircleOutlined style={{ color: '#1890ff' }} />}
            title="银行卡绑定申请已提交"
            subTitle="系统正在处理您的绑卡申请，请稍候..."
            extra={<Button type="primary" loading>处理中...</Button>}
          />
        </div>
      </Card>
    );
  }

  if (bindingStatus === 'reminder') {
    return (
      <Card title="第三步：银行卡绑定" className="step-card">
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <Space direction="vertical" size="large">
            <ClockCircleOutlined style={{ fontSize: 64, color: '#faad14' }} />
            <Title level={3}>请完成打款验证</Title>
              
            {/* 打款账户信息 */}
            <Card 
              title="打款账户信息" 
              style={{ textAlign: 'left', maxWidth: 500, margin: '0 auto' }}
              size="small"
            >
              <Descriptions column={1} size="small">
                <Descriptions.Item label="收款账户名称">
                  <Text strong>上海数据交易所有限公司</Text>
                  <Button 
                    type="link" 
                    size="small"
                    onClick={() => {
                      navigator.clipboard.writeText('上海数据交易所有限公司');
                      message.success('账户名称已复制');
                    }}
                  >
                    复制
                  </Button>
                </Descriptions.Item>
                <Descriptions.Item label="收款账号">
                  <Text strong style={{ fontFamily: 'monospace' }}>310904000018800001234</Text>
                  <Button 
                    type="link" 
                    size="small"
                    onClick={() => {
                      navigator.clipboard.writeText('310904000018800001234');
                      message.success('账号已复制');
                    }}
                  >
                    复制
                  </Button>
                </Descriptions.Item>
                <Descriptions.Item label="开户银行">交通银行上海分行营业部</Descriptions.Item>
                <Descriptions.Item label="打款金额">
                  <Text strong style={{ fontSize: '18px', color: '#f5222d' }}>
                    ¥{verificationAmount} 元
                  </Text>
                  <div style={{ marginTop: 4, fontSize: '12px', color: '#fa8c16' }}>
                    <strong>⚠️ 必须精确打款此金额，金额错误会导致验证失败</strong>
                  </div>
                  <Button 
                    type="link" 
                    size="small"
                    onClick={() => {
                      navigator.clipboard.writeText(verificationAmount);
                      message.success('打款金额已复制');
                    }}
                  >
                    复制金额
                  </Button>
                </Descriptions.Item>
                <Descriptions.Item label="附言说明">
                  <Text>银行卡验证 - {Date.now().toString().slice(-6)}</Text>
                  <Button 
                    type="link" 
                    size="small"
                    onClick={() => {
                      const remark = `银行卡验证 - ${Date.now().toString().slice(-6)}`;
                      navigator.clipboard.writeText(remark);
                      message.success('附言已复制');
                    }}
                  >
                    复制
                  </Button>
                </Descriptions.Item>
              </Descriptions>
            </Card>
  
            <Alert
              message="48小时内完成验证"
              description={
                <div>
                  <p>您的银行卡信息已提交，当前状态为“待打款验证”</p>
                  <p>请在48小时内使用上述账户信息向指定账户转账 <strong style={{color: '#f5222d'}}>¥{verificationAmount} 元</strong></p>
                  <p style={{color: '#ff4d4f', fontSize: '14px'}}><strong>⚠️ 重要提醒：必须精确打款 {verificationAmount} 元，不能是其他任意金额！</strong></p>
                  <p style={{color: '#fa8c16', fontSize: '12px'}}>系统将根据此精确金额进行自动验证，金额不符会导致验证失败</p>
                  <p>转账成功后，系统将自动验证并激活您的银行卡</p>
                </div>
              }
              type="warning"
              showIcon
              style={{ textAlign: 'left' }}
            />
            <Space>
              <Button type="primary" onClick={handleCompleteBinding}>
                我已完成打款验证
              </Button>
              <Button>稍后处理</Button>
            </Space>
          </Space>
        </div>
      </Card>
    );
  }

  return (
    <Card title="第三步：银行卡绑定" className="step-card">
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        requiredMark={false}
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item 
              name="bind_acct_no_acct_nme" 
              label="户名" 
              rules={[{ required: true, message: '请输入户名' }]}
            >
              <Input placeholder="请输入户名" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item 
              name="bind_acct_no" 
              label="银行卡号" 
              rules={[{ required: true, message: '请输入银行卡号' }]}
            >
              <Input placeholder="请输入银行卡号" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item 
              name="bind_acct_bank_no" 
              label="银行行号" 
              rules={[{ required: true, message: '请输入银行行号' }]}
            >
              <Input placeholder="请输入银行行号" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item 
              name="bind_acct_bank_nme" 
              label="银行名称" 
              rules={[{ required: true, message: '请输入银行名称' }]}
            >
              <Input placeholder="请输入银行名称" />
            </Form.Item>
          </Col>
        </Row>

        <Alert
          message="银行卡绑定说明"
          description="提交银行卡信息后，系统将向您的账户发起小额打款验证。请确保银行卡信息准确无误。"
          type="info"
          showIcon
          style={{ marginBottom: 24 }}
        />

        <Form.Item style={{ textAlign: 'center' }}>
          <Button 
            type="primary" 
            htmlType="submit" 
            size="large" 
            loading={loading}
            style={{ minWidth: 120 }}
          >
            提交银行卡信息
          </Button>
        </Form.Item>
      </Form>
    </Card>
  );
};

// 完成步骤
const CompletionStep: React.FC = () => {
  const { updateAccountStatus } = useAccount();

  React.useEffect(() => {
    // 更新账户状态为已完成
    updateAccountStatus({
      isAccountCreated: true,
      isLedgerCreated: true,
      isCardBound: true,
      memberInfo: {
        mshp_cust_id: 'CUST202509210001',
        plfm_bsn_id: 'PLTF001',
        cust_tp: 'B',
        cmpy_nme: '上海数据技术有限公司',
        cmpy_id_tp: '2011',
        cmpy_id_no: '91310000MA1FL2E15X',
        cmpy_id_due_dt: '20991231',
        org_inst_cd: 'MA1FL2E15X',
        rgs_addr: '上海市浦东新区张江高科技园区',
        opr_addr: '上海市浦东新区张江高科技园区',
        opr_scope: '数据处理和存储服务',
        bsn_lic_no: '91310000MA1FL2E15X',
        lgl_rprs_nme: '张三',
        lgl_rprs_id_tp: '1101',
        lgl_rprs_id_no: '310101199001011234',
        lgl_rprs_id_vld_dt: '20301231',
        lgl_rprs_phone_no: '13800138000',
        ath_agnt_nme: '李四',
        ath_agnt_id_tp: '1101',
        ath_agnt_id_no: '310101199002021234',
        ath_agnt_id_vld_dt: '20301231',
        ath_agnt_ctzn: 'CN',
        ath_agnt_mob_no: '13900139000',
        cust_sts: '1',
        vrfy_sts: '1',
        status: 'approved'
      }
    });
  }, [updateAccountStatus]);

  return (
    <Card className="step-card">
      <div style={{ textAlign: 'center', padding: '40px 0' }}>
        <Result
          status="success"
          title="账户开立完成！"
          subTitle="恭喜您，会员开立、登记簿开立和银行卡绑定全部完成，现在可以使用账户中心的所有功能。"
          extra={[
            <Button type="primary" key="center" onClick={() => window.location.href = '/account'}>
              进入账户中心
            </Button>,
            <Button key="transaction" onClick={() => window.location.href = '/transactions'}>
              查看交易记录
            </Button>
          ]}
        />
      </div>
    </Card>
  );
};

const AccountRegistration: React.FC = () => {
  const [current, setCurrent] = useState(0);

  const steps = [
    {
      title: '会员开立',
      icon: <UserOutlined />,
      description: '填写企业基本信息'
    },
    {
      title: '登记簿开立',
      icon: <SafetyOutlined />,
      description: '二维码授权验证'
    },
    {
      title: '银行卡绑定',
      icon: <BankOutlined />,
      description: '绑定企业银行卡'
    },
    {
      title: '开立完成',
      icon: <CheckCircleOutlined />,
      description: '账户开立成功'
    }
  ];

  const next = () => {
    setCurrent(current + 1);
  };

  const prev = () => {
    setCurrent(current - 1);
  };

  const renderStepContent = () => {
    switch (current) {
      case 0:
        return <MemberRegistrationStep current={current} onNext={next} onPrev={prev} />;
      case 1:
        return <LedgerRegistrationStep current={current} onNext={next} onPrev={prev} onReopen={() => setCurrent(0)} />;
      case 2:
        return <CardBindingStep current={current} onNext={next} onPrev={prev} />;
      case 3:
        return <CompletionStep />;
      default:
        return null;
    }
  };

  return (
    <div className="page-container">
      <div style={{ maxWidth: 1000, margin: '0 auto' }}>
        <Title level={2} style={{ textAlign: 'center', marginBottom: 32 }}>
          账户开立
        </Title>

        <Steps
          current={current}
          items={steps}
          style={{ marginBottom: 32 }}
        />

        {renderStepContent()}
      </div>
    </div>
  );
};

export default AccountRegistration;